import axios, { AxiosError } from 'axios';
import AuthResponse from '@/models/AuthResponse';
import Settings from '@/config/Settings';
import TokenResponse from '@/models/TokenResponse';
import { JsonConvert } from 'json2typescript';
import { useTokenStore } from '@/plugins/stores';
import AxiosHelper from '@/util/AxiosHelper';
import NavigationHelper from '@/util/NavigationHelper';
import { router } from '@/plugins/router';
import { Forms } from '@/enums/Forms';
import PageHelper from '@/util/PageHelper';

axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = false;
axios.defaults.headers.common = { 'Content-Type': 'application/json' };

export default class AuthController {
  private static readonly baseUrl = Settings.idUrl + `/api/auth/`;

  public static async signIn(username: string, password: string, querystring: string) {
    querystring = this.getQueryString(querystring);
    return axios
      .post(
        `${this.baseUrl}signin${querystring}`,
        `{
                "username": "${username}",
                "password": "${encodeURIComponent(password)}"
            }`
      )
      .then((response) => {
        if (response.status === 200) {
          const href = response.data as string;
          if (!href.startsWith(Settings.ownUrl)) {
            window.location.href = response.data as string;
            return AuthResponse.buildSuccesResponse(`Successful login`, `Login geslaagd`, '');
          }
          const code = (response.data as string).replaceAll(/^.*\?code=/g, '');
          return AuthResponse.buildSuccesResponse(`Successful login`, `Login geslaagd`, code);
        } else {
          return AuthResponse.buildFailedResponse(
            `Expected status 200 but got ${response.status}`,
            `Verwachte status is 200, maar ontvangen status is ${response.status}`
          );
        }
      })
      .catch((error: Error | AxiosError) => {
        return AxiosHelper.handleErrorResponse(error);
      });
  }

  public static async activate(username: string, password: string, code: string) {
    return axios
      .post(
        `${this.baseUrl}activate${this.getQueryString('')}`,
        `{
                "username": "${username}",
                "password": "${encodeURIComponent(password)}",
                "activationCode": "${code}"
            }`
      )
      .then((response) => {
        if (response.status === 200) {
          const code = (response.data as string).replaceAll(/^.*\?code=/g, '');
          return AuthResponse.buildSuccesResponse(`Successful login`, `Login geslaagd`, code);
        } else {
          return AuthResponse.buildFailedResponse(
            `Expected status 200 but got ${response.status}`,
            `Verwachte status is 200, maar ontvangen status is ${response.status}`
          );
        }
      })
      .catch((error: Error | AxiosError) => {
        return AxiosHelper.handleErrorResponse(error);
      });
  }

  public static async signUp(
    firstName: string,
    lastName: string,
    company: string,
    emailAddress: string,
    phoneNumber: string,
    password: string
  ): Promise<AuthResponse> {
    return axios
      .post(
        `${this.baseUrl}signup`,
        `{
              "firstname": "${firstName}",
              "lastname": "${lastName}",
              "company": "${company}",
              "email": "${emailAddress}",
              "phonenumber": "${phoneNumber}",
              "password": "${encodeURIComponent(password)}"
             }`
      )
      .then((response) => {
        if (response.status === 201) {
          return AuthResponse.buildSuccesResponse(
            `User created, you need to activate your account. See your e-mail.`,
            `Gebruiker aangemaakt, u moet uw account activeren (zie e-mail)`
          );
        } else {
          return AuthResponse.buildFailedResponse(
            `Expected status 201 but got ${response.status}`,
            `Verwachte status is 201, maar ontvangen status is ${response.status}`
          );
        }
      })
      .catch(() => {
        return AuthResponse.buildFailedResponse(`User creation failed`, `Gebruiker aanmaken is mislukt`);
      });
  }

  public static async getTokens(code: string) {
    const redirectUri = new URL(window.location.href);
    redirectUri.search = '';
    redirectUri.hash = '';
    let redirectUriString = redirectUri.toString();
    redirectUriString = redirectUriString.endsWith('/')
      ? redirectUriString.substring(0, redirectUriString.length - 1)
      : redirectUriString;

    return axios
      .post(`${this.baseUrl}token?grant_type=authorization_code&code=${code}&redirect_uri=${redirectUriString}`)
      .then((response) => {
        if (response.status === 200) {
          return new JsonConvert().deserializeObject(response.data, TokenResponse);
        } else {
          return AuthResponse.buildFailedResponse(
            `Expected status 200 but got ${response.status}`,
            `Verwachte status is 200, maar ontvangen status is ${response.status}`
          );
        }
      })
      .catch((error: Error | AxiosError) => {
        return AxiosHelper.handleErrorResponse(error);
      });
  }

  public static async refresh() {
    const response = await axios
      .post(`${this.baseUrl}token?grant_type=refresh_token&refresh_token=&scope=openid`)
      .catch(async (error: Error | AxiosError) => {
        PageHelper.setNotificationMessage(
          AuthResponse.buildFailedResponse(
            'Refresh tokens failed, please login',
            'Refreh van tokens mislukt, log opnieuw in'
          )
        );
        await NavigationHelper.openForm(router, Forms.SIGN_IN);
        return AxiosHelper.handleErrorResponse(error);
      });
    if (response.status === 200) {
      if (localStorage.getItem('idToken') !== '') {
        const tokenStore = useTokenStore();
        const data = response.data as Record<string, string>;
        tokenStore.accessToken = data.access_token;
      }
    }
    return response;
  }

  public static async cookieSignIn(querystring: string) {
    querystring = this.getQueryString(querystring);
    querystring = `${querystring}&portal_id=USER_PORTAL&portal_uri=${Settings.ownUrl}`;
    return axios
      .get(`${this.baseUrl}signin${querystring}`)
      .then((response) => {
        if (response.status === 200) {
          const data = response.data as Record<string, string>;
          if (JSON.stringify(data).toLowerCase().indexOf('login required') >= 0) {
            return AuthResponse.buildFailedResponse(JSON.stringify(data), JSON.stringify(data));
          }
          window.location.href = response.data ? (response.data as string) : '';
          return AuthResponse.buildSuccesResponse(JSON.stringify(data), JSON.stringify(data));
        } else {
          return AuthResponse.buildFailedResponse(
            `Expected status 200 but got ${response.status}`,
            `Verwachte status is 200, maar ontvangen status is ${response.status}`
          );
        }
      })
      .catch((error: Error | AxiosError) => {
        return AxiosHelper.handleErrorResponse(error);
      });
  }

  public static async logout() {
    return await axios.get(`${this.baseUrl}logout`);
  }

  private static getQueryString(querystring: string) {
    if (querystring === '') {
      const locationUrl = window.location.href;
      const href: string = locationUrl.endsWith('/') ? locationUrl.substring(0, locationUrl.length - 1) : locationUrl;
      querystring = `?client_id=USER_PORTAL&redirect_uri=${href}`;
    }
    return querystring;
  }
}
